import { useEffect, useId, useRef, useState } from 'react';
import { Button, Dialog } from '../ui';
import { dueDay, pacificDate, wordsOf } from '../todos/todoFormat';
import { blanksLeft, splitBlanks } from '../../utils/draftBlanks';
import { api, type ListedSendCard, type MorningAttachPick, type SendCard } from '../../lib/api';

/**
 * MORNING2 (Task 8) - the email send card: a reply the AI drafted in his morning chat, which HE approves.
 * Spec docs/planning/2026-10-27-morning-chat-design.md (4.4); plan docs/planning/2026-10-28-morning2-plan.md (Task 8).
 *  - ⭐ Shows every recipient (To, and Cc and Bcc whenever not empty, each name and address in full), the subject, the
 *    body with each STORED blank on a yellow mark (his own "[see attached]" stays plain), what is attached, and the files
 *    the chat offered as unticked boxes.
 *  - ⛔ Send is off while a stored blank is still in the text ("Fill the yellow part first") and while Edit is open.
 *  - ⛔⛔ This is the screen side of the ONLY path that sends mail from his mailbox: Send opens a dialog naming EVERY To,
 *    Cc and Bcc recipient in full, and only the dialog's Send posts - `confirm: true` with the version of the LAST save
 *    (the card's own when nothing was saved), taken when the dialog opened.
 *  - ⛔ Every PUT the card makes (Edit's Save, a tick on an offered file) sends To, Cc, Bcc, subject and body together
 *    with every saved pick: a Cc or Bcc is never dropped by a save, nor an attachment by a tick (rulings P9, P11). Edit
 *    checks each recipient is wholly one address before it saves (P12) and says which one is not.
 *  - ⛔ A refusal (409 / 502) shows the server's words and keeps the card as it was.
 *  - Phone first (375 px): no fixed width, the body and every address break their words, the buttons wrap.
 */

const PACIFIC = 'America/Los_Angeles';
const CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: PACIFIC, hour: 'numeric', minute: '2-digit', hour12: true });

const FILL = 'Fill the yellow part first';
const EDIT_OPEN = 'Save or cancel the edit first.';
// ⛔ Files added in Gmail are not carried by Edit and Save (the card's ticks are what it attaches) - so the line says so.
const CHANGED_IN_GMAIL = 'Changed in Gmail since you saw it - open Edit and Save to use this version (files added in Gmail are not kept - tick them here).';
const REFUSED = 'Not sent - the draft stays in Gmail Drafts.';
// ⛔ Must match the 409 words in backend/src/services/mailDraftService.ts (CHANGED): the hint under it keys on them.
const CHANGED = 'The draft changed - check it and tap Send again.';
const CHANGED_HINT = 'To send what this card shows, open Edit and Save - it replaces the Gmail draft.';
// The server's own words for an empty To (mailDraftService NO_TO).
const NO_TO = 'Say who it goes to.';
const NOT_SAVED = 'Not saved - try again.';
const NOT_SENT = 'Not sent - try again.';
const NOT_REFUSED = "Couldn't mark it not sent - try again.";
const NOT_TICKED = "Couldn't tick it off - try again.";
const NOT_UNDONE = "Couldn't undo it - try again.";
/** Task 9: the chat re-emitted this card while Edit is open - his edit is kept, and Cancel shows the chat's version. */
const CHANGED_BY_CHAT = 'Changed by the chat - Cancel to see it';

const PILL = 'inline-flex max-w-full items-center justify-center rounded-full px-4 py-1.5 text-left text-[13px] leading-snug break-words transition-colors disabled:cursor-not-allowed disabled:opacity-40';
const OUTLINE = `${PILL} border border-gray-300 bg-white font-medium text-gray-800 hover:border-gray-900`;
const DARK = `${PILL} bg-gray-900 font-semibold text-white hover:bg-gray-800`;
const FIELD = 'block w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[15px] text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:bg-gray-50';
const LABEL = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';
const ALERT = 'mt-2 break-words rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700';
const LINK = 'text-[12.5px] font-medium text-gray-700 underline underline-offset-2 break-words hover:text-gray-900';

/**
 * MIRRORED BY HAND from backend/src/services/gmailInbox.ts `splitAddresses` - the frontend never imports backend source.
 * One header into its recipients: a comma inside quotes or inside <...> does not split.
 */
const splitAddresses = (header: string | null): string[] => {
  if (!header) return [];
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  let angle = 0;
  for (const ch of header) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && ch === '<') angle += 1;
    else if (!quoted && ch === '>') angle = Math.max(0, angle - 1);
    if (ch === ',' && !quoted && angle === 0) {
      if (cur.trim()) out.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
};

/**
 * MIRRORED BY HAND from backend/src/services/mailDraftService.ts (`MAX_ADDRESS_ITEM`, `isAddress`, `NAMED_ADDRESS`,
 * `oneWholeAddress`; rulings P12 and P14, backend commit 4c209ace): a recipient is WHOLLY one address - a bare address,
 * or `Name <address>` and nothing after it - and at most 998 characters (RFC 5322's line limit), checked BEFORE any
 * pattern runs. The server refuses anything else; the card checks first so it can say which one. Change those and change
 * these, character for character.
 */
const MAX_ADDRESS_ITEM = 998;
const isAddress = (s: string) => s.length <= MAX_ADDRESS_ITEM && /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/.test(s);
const NAMED_ADDRESS = /^(?:"[^"<>\\\r\n]*"\s*|[^"<>@,;:\\\r\n]*)<([^<>\s]+)>$/;
const oneWholeAddress = (item: string): boolean => {
  const s = item.trim();
  if (s.length > MAX_ADDRESS_ITEM) return false;
  if (isAddress(s)) return true;
  const m = s.match(NAMED_ADDRESS);
  return !!m && isAddress(m[1]);
};

/** A recipient as the words quote it: a long one is cut, so the line names it without a wall of text. */
const quoted = (item: string) => (item.length > 60 ? `${item.slice(0, 57)}...` : item);

/** The first thing wrong with what Edit would save, in words - or null. */
const recipientProblem = (to: string, cc: string, bcc: string): string | null => {
  if (!splitAddresses(to).length) return NO_TO;
  const fields: Array<[string, string]> = [['To', to], ['Cc', cc], ['Bcc', bcc]];
  for (const [field, value] of fields) {
    const bad = splitAddresses(value).find((item) => !oneWholeAddress(item));
    if (bad) return `Check ${field}: "${quoted(bad)}" - write each one as Name <address> or just the address.`;
  }
  return null;
};

/**
 * "Robert Mendieta <robert@apex-res.com>" -> "Robert"; "\"Mendieta, Robert\" <robert@...>" -> "Robert"; a bare address
 * stays the address.
 * ⭐ P14: SCANNED, not matched - the name is found the way the backend's `nameOf` finds it (mailDraftService.ts), because
 *   a pattern like `^\s*"?([^"<]*?)"?\s*<...>` is quadratic on a long run of spaces, and a To read back from Gmail is
 *   untrusted. Then the first word of the name (after the comma, for "Last, First").
 */
const firstNameOf = (item: string): string => {
  const s = item.trim();
  const fallback = s.replace(/[<>]/g, '').trim();
  const lt = s.indexOf('<');
  // One <...> holding at least one character, closed by the LAST character and by no '>' before it.
  if (lt < 0 || s.length - lt < 3 || s.indexOf('>', lt + 1) !== s.length - 1) return fallback;
  let name = s.slice(0, lt).trimEnd();
  if (name.startsWith('"')) name = name.slice(1);
  if (name.endsWith('"')) name = name.slice(0, -1);
  name = name.trim();
  if (!name || name.includes('"')) return fallback;
  const given = name.includes(',') ? name.slice(name.indexOf(',') + 1).trim() || name : name;
  return given.split(/\s+/)[0];
};

/**
 * "7:42 AM" in Pacific, whatever the device's zone - with the day ("Oct 27, 7:42 AM") when it was not today in Pacific.
 * ⭐ Built from its parts: newer browsers put a narrow no-break space before "AM".
 */
const sentWhen = (iso: string, now: Date): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const p = Object.fromEntries(CLOCK.formatToParts(at).map((x) => [x.type, x.value]));
  const clock = `${p.hour}:${p.minute} ${p.dayPeriod}`;
  return pacificDate(at) === pacificDate(now) ? clock : `${dueDay(iso, now)}, ${clock}`;
};

const samePick = (a: MorningAttachPick, b: MorningAttachPick): boolean =>
  a.kind === 'mail'
    ? b.kind === 'mail' && a.threadId === b.threadId && a.attachmentId === b.attachmentId
    : b.kind === 'file' && a.fileId === b.fileId;
const pickKey = (p: MorningAttachPick) => (p.kind === 'mail' ? `mail:${p.threadId}:${p.attachmentId}` : `file:${p.fileId}`);

type Form = { to: string; cc: string; bcc: string; subject: string; body: string };
/** A to-do after a send: ticked off (Undo), offered (one tap), or put back on his list by Undo. */
type TodoRow = { task_id: string; title: string; state: 'ticked' | 'offered' | 'reopened' };
/** What the dialog was opened with - the draft, the version and the recipients it names. ⛔ Its Send posts exactly this. */
type Confirm = { draftId: string; to: string[]; cc: string[]; bcc: string[]; subject: string; version: string };

/**
 * A pill's label. ⚠️ A flex item will not shrink below its longest word, so a long bare address ("Send to
 * a-long-mailbox@...") would push the pill past a 375 px column: `min-w-0` lets it shrink, `overflow-wrap: anywhere`
 * breaks the word.
 */
const PillLabel = ({ children }: { children: string }) => (
  <span className="min-w-0 break-words [overflow-wrap:anywhere]">{children}</span>
);

/** One "To / Cc / Bcc / Subject" line: the label, then the value in full, wrapping on a phone. */
const Line = ({ term, value }: { term: string; value: string }) => (
  <div className="flex min-w-0 items-baseline gap-2">
    <dt className="w-14 shrink-0 text-[12px] font-medium text-gray-500">{term}</dt>
    <dd className="min-w-0 flex-1 break-words text-[13px] text-gray-900">{value}</dd>
  </div>
);

export default function MorningSendCard({ card, onChange, onSent, disabled = false }: {
  card: ListedSendCard;
  /** The card as it is now - after a save (new version), a send or Don't send. The parent keeps it. */
  onChange: (c: SendCard) => void;
  /**
   * Task 9: an email went - told on EVERY send, also when the chat had meanwhile swapped this card for another draft
   * (which is then not stamped Sent). The Morning page reads its list again.
   */
  onSent?: () => void;
  /** True while an answer is still arriving: the chat may be rewriting this very draft. */
  disabled?: boolean;
}) {
  const ids = useId();
  /** Edit's fields; null = Edit is closed. */
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [sending, setSending] = useState(false);
  const [refusing, setRefusing] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [todos, setTodos] = useState<TodoRow[]>([]);
  const [tickWords, setTickWords] = useState<string | null>(null);
  const [todoBusy, setTodoBusy] = useState<string | null>(null);
  const [todoError, setTodoError] = useState<string | null>(null);
  /** The card as it is NOW - a send's answer can arrive after the chat handed the page another draft. */
  const latest = useRef(card);
  latest.current = card;
  /** What Edit opened on - so a card the chat re-emits meanwhile is SAID, never silently kept under his edit. */
  const [editBase, setEditBase] = useState<Form & { draftId: string } | null>(null);
  /** A send that went for a draft this card no longer shows: who it went to. */
  const [sentEarlier, setSentEarlier] = useState<string | null>(null);

  // ⛔ A chat turn starting (`disabled`) closes a confirm not yet tapped: the chat may be rewriting this very draft, and
  //    no answer may race a send. A send already posted is left to finish.
  useEffect(() => {
    if (disabled && !sending) setConfirm(null);
  }, [disabled, sending]);

  const label = card.subject ? `Email reply - ${card.subject}` : 'Email reply';
  const shell = (extra = '') => `mt-1 w-full max-w-full min-w-0 rounded-[20px] border bg-white p-3 text-left shadow-sm sm:p-4 ${extra}`;
  const gmailLink = <a href={card.gmail_url} target="_blank" rel="noopener noreferrer" className={LINK}>Open in Gmail</a>;
  const subjectLine = card.subject ? <p className="break-words text-[13px] font-semibold text-gray-900">{card.subject}</p> : null;

  // ⛔ A card whose Gmail draft could not be read shows the words and nothing he could send.
  if (card.unreadable) {
    return (
      <section aria-label={label} className={shell('border-gray-200')}>
        <p className="break-words rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">{card.unreadable}</p>
        <div className="mt-2">{gmailLink}</div>
      </section>
    );
  }

  if (card.status === 'refused') {
    return (
      <section aria-label={label} className={shell('border-gray-200')}>
        {subjectLine}
        <p className="break-words text-[13px] text-gray-500">{REFUSED}</p>
      </section>
    );
  }

  /** After a send: the to-do it ticked off (Undo puts it back), or the ones it offers - one tap each. */
  const todoAct = async (row: TodoRow, how: 'done' | 'reopen') => {
    setTodoBusy(row.task_id);
    setTodoError(null);
    try {
      if (how === 'done') await api.todos.done(row.task_id);
      else await api.todos.reopen(row.task_id);
      setTodos((rows) => rows.map((r) => (r.task_id === row.task_id ? { ...r, state: how === 'done' ? 'ticked' : 'reopened' } : r)));
    } catch (e) {
      setTodoError(wordsOf(e, how === 'done' ? NOT_TICKED : NOT_UNDONE));
    } finally {
      setTodoBusy(null);
    }
  };

  /** After a send: tick_words, and the to-do it ticked off (Undo puts it back) or the ones it offers - one tap each. */
  const todoResult = (
    <>
      {tickWords && <p className="mt-2 break-words text-[12.5px] text-amber-800">{tickWords}</p>}
      {todos.length > 0 && (
        <ul className="mt-2 min-w-0 space-y-1.5">
          {todos.map((t) => (
            <li key={t.task_id} className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-gray-800">
              {t.state === 'ticked' && (
                <>
                  <span className="min-w-0 break-words">{`Ticked off: ${t.title}`}</span>
                  <span aria-hidden="true">-</span>
                  <button type="button" className={LINK} disabled={todoBusy !== null} onClick={() => void todoAct(t, 'reopen')}>Undo</button>
                </>
              )}
              {t.state === 'offered' && (
                <button type="button" className={OUTLINE} disabled={todoBusy !== null} onClick={() => void todoAct(t, 'done')}>
                  <PillLabel>{`Tick off "${t.title}" too?`}</PillLabel>
                </button>
              )}
              {t.state === 'reopened' && <span className="min-w-0 break-words">{`Back on your tasks: ${t.title}`}</span>}
            </li>
          ))}
        </ul>
      )}
      {todoError && <p role="alert" className={ALERT}>{todoError}</p>}
    </>
  );

  if (card.status === 'sent') {
    const items = splitAddresses(card.sent_to);
    const who = items.length ? ` to ${firstNameOf(items[0])}` : '';
    const when = card.sent_at ? sentWhen(card.sent_at, new Date()) : '';
    return (
      <section aria-label={label} className={shell('border-green-200')}>
        {subjectLine}
        <p className="break-words text-[13px] font-semibold text-green-700">{`Sent${who}${when ? ` - ${when}` : ''}`}</p>
        {items.length > 0 && <p className="mt-0.5 break-words text-[12px] text-gray-600">{items.join(', ')}</p>}
        <div className="mt-2">{gmailLink}</div>
        {todoResult}
      </section>
    );
  }

  // -- An open card --
  const toItems = splitAddresses(card.to);
  const ccItems = splitAddresses(card.cc);
  const bccItems = splitAddresses(card.bcc);
  const left = blanksLeft(card.body, card.blanks);
  const editing = form !== null;
  const busy = disabled || saving || sending || refusing || ticking;
  const canSend = !busy && !editing && left.length === 0 && toItems.length > 0;
  const hint = editing ? EDIT_OPEN : left.length ? FILL : null;
  const first = toItems.length ? firstNameOf(toItems[0]) : '';

  // What is attached, less the offered files he ticked (those show as their own ticked box below).
  const isPicked = (p: MorningAttachPick) => card.attach.some((a) => samePick(a, p));
  const attachedNames = card.attachments.map((a) => a.name);
  for (const o of card.offered) {
    if (!isPicked(o.pick)) continue;
    const at = attachedNames.indexOf(o.name);
    if (at >= 0) attachedNames.splice(at, 1);
  }

  const openEdit = () => {
    setError(null);
    setEditError(null);
    setForm({ to: toItems.join(', '), cc: ccItems.join(', '), bcc: bccItems.join(', '), subject: card.subject, body: card.body });
    setEditBase({ to: card.to, cc: card.cc, bcc: card.bcc, subject: card.subject, body: card.body, draftId: card.draft_id });
  };
  const closeEdit = () => { setForm(null); setEditBase(null); };
  /** The chat changed the draft under an open Edit (a revise re-emits the card): his edit stays, the line says so. */
  const changedByChat = editing && editBase !== null && (
    editBase.draftId !== card.draft_id || editBase.to !== card.to || editBase.cc !== card.cc || editBase.bcc !== card.bcc
    || editBase.subject !== card.subject || editBase.body !== card.body
  );

  /** Edit's Save: exactly what Edit shows, with every saved pick. ⛔ Saving never sends. */
  const saveEdit = async () => {
    if (!form) return;
    const edit = { to: form.to.trim(), cc: form.cc.trim(), bcc: form.bcc.trim(), subject: form.subject, body: form.body };
    const problem = recipientProblem(edit.to, edit.cc, edit.bcc);
    if (problem) {
      setEditError(problem);
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      const r = await api.morning.saveDraft(card.draft_id, { ...edit, attach: card.attach });
      closeEdit();
      setError(null);
      onChange({ ...card, ...edit, attachments: r.attachments, version: r.version, changed_in_gmail: false });
    } catch (e) {
      setEditError(wordsOf(e, NOT_SAVED));
    } finally {
      setSaving(false);
    }
  };

  /** A tick on an offered file saves the card as it shows, with every saved pick plus (or less) this one. */
  const toggleOffered = async (pick: MorningAttachPick) => {
    const attach = isPicked(pick) ? card.attach.filter((a) => !samePick(a, pick)) : [...card.attach, pick];
    setTicking(true);
    setError(null);
    try {
      const r = await api.morning.saveDraft(card.draft_id, {
        to: card.to, cc: card.cc, bcc: card.bcc, subject: card.subject, body: card.body, attach,
      });
      onChange({ ...card, attach, attachments: r.attachments, version: r.version, changed_in_gmail: false });
    } catch (e) {
      setError(wordsOf(e, NOT_SAVED));
    } finally {
      setTicking(false);
    }
  };

  /** Send: only ASKS. The dialog names what the card shows now and keeps the draft and version it was shown with. */
  const askToSend = () => {
    setError(null);
    setConfirm({
      draftId: card.draft_id, to: toItems, cc: ccItems, bcc: bccItems, subject: card.subject, version: card.version,
    });
  };

  /** ⛔⛔ The dialog's Send - the only place this card sends mail: the draft and version the dialog was opened with. */
  const send = async () => {
    if (!confirm || disabled) return;
    setSending(true);
    try {
      const r = await api.morning.sendDraft(confirm.draftId, confirm.version);
      setConfirm(null);
      setTickWords(r.tick_words ?? null);
      setTodos([
        ...(r.ticked ? [{ task_id: r.ticked.task_id, title: r.ticked.title, state: 'ticked' as const }] : []),
        ...(r.offer_tick ?? []).map((t) => ({ task_id: t.task_id, title: t.title, state: 'offered' as const })),
      ]);
      onSent?.();
      // ⛔ Task 9: stamp the card Sent only if it still shows the draft that went (the snapshot) - the chat may have handed
      //   the page another draft meanwhile, and that one was never sent. The to-do result shows either way.
      if (latest.current.draft_id !== confirm.draftId) {
        setSentEarlier(r.sentTo);
        return;
      }
      onChange({ ...latest.current, status: 'sent', sent_to: r.sentTo, sent_at: new Date().toISOString() });
    } catch (e) {
      setConfirm(null);
      setError(wordsOf(e, NOT_SENT));
    } finally {
      setSending(false);
    }
  };

  /** Don't send: the draft is marked refused. ⛔ Nothing is sent; the Gmail draft stays in Drafts. */
  const refuse = async () => {
    setRefusing(true);
    setError(null);
    try {
      await api.morning.refuseDraft(card.draft_id);
      onChange({ ...card, status: 'refused' });
    } catch (e) {
      setError(wordsOf(e, NOT_REFUSED));
    } finally {
      setRefusing(false);
    }
  };

  const field = (key: keyof Form, name: string) => (
    <div>
      <label htmlFor={`${ids}-${key}`} className={LABEL}>{name}</label>
      <input
        id={`${ids}-${key}`}
        className={FIELD}
        value={form?.[key] ?? ''}
        disabled={saving}
        onChange={(e) => setForm((f) => (f ? { ...f, [key]: e.target.value } : f))}
      />
    </div>
  );

  return (
    <>
      <section aria-label={label} className={shell('border-gray-200')}>
        {form ? (
          <div className="min-w-0 space-y-2">
            {field('to', 'To')}
            {field('cc', 'Cc')}
            {field('bcc', 'Bcc')}
            {field('subject', 'Subject')}
            <div>
              <label htmlFor={`${ids}-body`} className={LABEL}>Message</label>
              <textarea
                id={`${ids}-body`}
                className={`${FIELD} min-h-[10rem]`}
                rows={8}
                value={form.body}
                disabled={saving}
                onChange={(e) => setForm((f) => (f ? { ...f, body: e.target.value } : f))}
              />
            </div>
            {editError && <p role="alert" className={ALERT}>{editError}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={DARK} disabled={saving || disabled} onClick={() => void saveEdit()}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" className={OUTLINE} disabled={saving} onClick={() => { closeEdit(); setEditError(null); }}>
                Cancel
              </button>
            </div>
            {changedByChat && (
              <p role="status" className="break-words rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
                {CHANGED_BY_CHAT}
              </p>
            )}
          </div>
        ) : (
          <>
            <dl className="min-w-0 space-y-0.5">
              <Line term="To" value={toItems.join(', ')} />
              {ccItems.length > 0 && <Line term="Cc" value={ccItems.join(', ')} />}
              {bccItems.length > 0 && <Line term="Bcc" value={bccItems.join(', ')} />}
              <Line term="Subject" value={card.subject} />
            </dl>
            <div className="mt-3 whitespace-pre-wrap break-words border-t border-gray-100 pt-3 text-[14px] leading-relaxed text-gray-900">
              {splitBlanks(card.body, card.blanks).map((s, i) => (s.blank
                ? <mark key={i} className="rounded bg-yellow-200 px-0.5 text-gray-900">{s.text}</mark>
                : <span key={i}>{s.text}</span>))}
            </div>
          </>
        )}

        {(attachedNames.length > 0 || card.offered.length > 0) && (
          <ul className="mt-3 min-w-0 space-y-1 border-t border-gray-100 pt-2">
            {attachedNames.map((name, i) => (
              <li key={`attached-${i}`}>
                <label className="flex min-w-0 items-start gap-2 text-[13px] text-gray-800">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked disabled readOnly />
                  <span className="min-w-0 break-words">{name}</span>
                </label>
              </li>
            ))}
            {card.offered.map((o) => (
              <li key={pickKey(o.pick)}>
                <label className="flex min-w-0 items-start gap-2 text-[13px] text-gray-800">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    checked={isPicked(o.pick)}
                    disabled={busy}
                    onChange={() => void toggleOffered(o.pick)}
                  />
                  <span className="min-w-0 break-words">{`Attach "${o.name}"`}</span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {sentEarlier && (
          <>
            <p role="status" className="mt-3 break-words rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-[12.5px] text-green-800">
              {`Sent to ${sentEarlier} - the chat has changed this card since.`}
            </p>
            {todoResult}
          </>
        )}
        {card.changed_in_gmail && (
          <p className="mt-3 break-words rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
            {CHANGED_IN_GMAIL}
          </p>
        )}
        {error && (
          <div role="alert" className={ALERT}>
            <span>{error}</span>
            {error === CHANGED && <span className="mt-1 block text-[12px]">{CHANGED_HINT}</span>}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" className={OUTLINE} disabled={busy || editing} onClick={() => void refuse()}>
            Don&apos;t send
          </button>
          {!editing && (
            <button type="button" className={OUTLINE} disabled={busy} onClick={openEdit}>Edit</button>
          )}
          <button type="button" className={DARK} disabled={!canSend} onClick={askToSend}>
            <PillLabel>{first ? `Send to ${first}` : 'Send'}</PillLabel>
          </button>
        </div>
        {hint && <p className="mt-1.5 break-words text-[12px] text-gray-600">{hint}</p>}
      </section>

      {/* ⛔⛔ The confirm names every recipient in full; only its Send sends. */}
      <Dialog
        open={!!confirm}
        onClose={() => { if (!sending) setConfirm(null); }}
        size="sm"
        dismissible={!sending}
        title={confirm ? `Send to ${[...confirm.to, ...confirm.cc, ...confirm.bcc].join(', ')}?` : ''}
        footer={(
          <>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={sending}>Cancel</Button>
            <Button onClick={() => void send()} isLoading={sending} disabled={disabled}>Send</Button>
          </>
        )}
      >
        {confirm && (
          <dl className="min-w-0 space-y-0.5">
            <Line term="To" value={confirm.to.join(', ')} />
            {confirm.cc.length > 0 && <Line term="Cc" value={confirm.cc.join(', ')} />}
            {confirm.bcc.length > 0 && <Line term="Bcc" value={confirm.bcc.join(', ')} />}
            <Line term="Subject" value={confirm.subject} />
          </dl>
        )}
      </Dialog>
    </>
  );
}
